// Shared navigation model for the Home v2 shell.
// Both the desktop Sidebar and the mobile BottomTabBar render from these
// definitions so the two navigations never drift. Routes point at the
// existing web routes (transfer/receive/shield/swap/...) — real flow, no stubs.
import { SvgProps } from 'react-native-svg'

import ActivityIcon from '@common/assets/svg/ActivityIcon'
import DAppsIcon from '@common/assets/svg/DAppsIcon'
import GlobeIcon from '@common/assets/svg/GlobeIcon'
import ReceiveIcon from '@common/assets/svg/ReceiveIcon'
import SendIcon from '@common/assets/svg/SendIcon'
import SettingsIcon from '@common/assets/svg/SettingsIcon'
import ShieldFilledIcon from '@common/assets/svg/ShieldFilledIcon'
import SwapAndBridgeIcon from '@common/assets/svg/SwapAndBridgeIcon'
import WalletFilledIcon from '@common/assets/svg/WalletFilledIcon'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import { FEATURES } from '@web/constants/features'

export type NavKey =
  | 'home'
  | 'send'
  | 'swap'
  | 'receive'
  | 'shield'
  | 'unshield'
  | 'apps'
  | 'walletconnect'
  | 'activity'
  | 'settings'

export type NavItem = {
  key: NavKey
  label: string
  icon: React.FC<SvgProps>
  /** Target route (omitted for the home tab itself / handled inline). */
  route?: string
  /**
   * Only surface this entry in PUBLIC mode. Currently unused — Swap & Bridge is
   * shown in BOTH modes now that PRIVATE mode has a railgun "swap from private"
   * flow (the swap screen renders the RailgunSwapPanel when eligible; the public
   * on-chain form when not). Kept for future public-only entries.
   */
  publicOnly?: boolean
  /**
   * This entry drives a private-wallet (RAILGUN) flow — shield / unshield / the
   * private send. Disabled (with an explanatory tooltip) when the selected
   * account can't have a private wallet, e.g. it was imported from a raw private
   * key. See isPrivateModeBlocked / usePrivateModeReason.
   */
  privateAction?: boolean
  /** Show a "New" badge next to the label (sidebar, expanded only). */
  badge?: boolean
}

export type NavGroup = {
  label: string
  items: NavItem[]
}

// Sidebar groups (desktop). Mirrors the design's Wallet / Privacy / More sections.
export const SIDEBAR_GROUPS: NavGroup[] = [
  {
    label: 'Wallet',
    items: [
      { key: 'home', label: 'Home', icon: WalletFilledIcon, route: WEB_ROUTES.dashboard },
      { key: 'send', label: 'Send', icon: SendIcon, route: WEB_ROUTES.transfer, privateAction: true },
      // Public Swap & Bridge, re-enabled in the production mobile-web build
      // (ZKX product decision, 2026-07-07). Private (RAILGUN) swap stays
      // dev-only — see `mobileWebPrivateSwapEnabled` in features.ts.
      ...(FEATURES.mobileWebSwapBridgeEnabled
        ? [
            {
              key: 'swap' as const,
              label: 'Swap & Bridge',
              icon: SwapAndBridgeIcon,
              route: WEB_ROUTES.swapAndBridge
            }
          ]
        : []),
      { key: 'receive', label: 'Receive', icon: ReceiveIcon, route: WEB_ROUTES.receive }
    ]
  },
  {
    label: 'Privacy',
    items: [
      { key: 'shield', label: 'Shield', icon: ShieldFilledIcon, route: WEB_ROUTES.shield, privateAction: true, badge: true },
      { key: 'unshield', label: 'Unshield', icon: GlobeIcon, route: WEB_ROUTES.unshield, privateAction: true, badge: true }
    ]
  },
  {
    label: 'More',
    items: [
      // Apps (dApp catalog), re-enabled in the production sidebar (ZKX
      // product decision, 2026-07-07). WalletConnect removed from the
      // sidebar entirely.
      ...(FEATURES.mobileWebDappsEnabled
        ? [{ key: 'apps' as const, label: 'Apps', icon: DAppsIcon, route: WEB_ROUTES.apps }]
        : []),
      { key: 'activity', label: 'Activity', icon: ActivityIcon, route: WEB_ROUTES.transactions },
      { key: 'settings', label: 'Settings', icon: SettingsIcon, route: WEB_ROUTES.generalSettings }
    ]
  }
]

// Bottom tab bar (mobile) — four destinations flanking a center FAB:
// [Home, Activity] · (+) · [Receive|Apps, Setting]. The FAB opens FAB_ACTIONS.
// Apps (dApp) re-enabled in prod (ZKX product decision, 2026-07-07) → takes
// the third slot instead of Receive whenever `mobileWebDappsEnabled` is on.
export const BOTTOM_TABS: NavItem[] = [
  { key: 'home', label: 'Home', icon: WalletFilledIcon, route: WEB_ROUTES.dashboard },
  { key: 'activity', label: 'Activity', icon: ActivityIcon, route: WEB_ROUTES.transactions },
  FEATURES.mobileWebDappsEnabled
    ? { key: 'apps', label: 'Apps', icon: DAppsIcon, route: WEB_ROUTES.apps }
    : { key: 'receive', label: 'Receive', icon: ReceiveIcon, route: WEB_ROUTES.receive },
  // Mobile lands on the settings menu grid; desktop sidebar opens General directly.
  { key: 'settings', label: 'Setting', icon: SettingsIcon, route: WEB_ROUTES.settingsMenu }
]

// Center FAB action menu (mobile) — the privacy/transfer quick actions that pop
// up above the tab bar when the + button is pressed. Order = top → bottom.
export const FAB_ACTIONS: NavItem[] = [
  // WalletConnect removed from the FAB menu entirely (ZKX product decision).
  { key: 'unshield', label: 'Unshield', icon: GlobeIcon, route: WEB_ROUTES.unshield, privateAction: true },
  { key: 'shield', label: 'Shield', icon: ShieldFilledIcon, route: WEB_ROUTES.shield, privateAction: true },
  { key: 'receive', label: 'Receive', icon: ReceiveIcon, route: WEB_ROUTES.receive },
  // Public Swap & Bridge, re-enabled in the production FAB menu (ZKX product
  // decision, 2026-07-07).
  ...(FEATURES.mobileWebSwapBridgeEnabled
    ? [
        {
          key: 'swap' as const,
          label: 'Swap & Bridge',
          icon: SwapAndBridgeIcon,
          route: WEB_ROUTES.swapAndBridge
        }
      ]
    : []),
  { key: 'send', label: 'Send', icon: SendIcon, route: WEB_ROUTES.transfer, privateAction: true }
]
